import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Bell, CheckCircle, AlertCircle, Info, Trash2, CheckCheck, Award, Calendar, Video, FileText } from "lucide-react";

const Notifications = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        params: { unread_only: filter === "unread" },
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
      toast.success("Marked as read");
    } catch (error) {
      toast.error("Failed to update notification");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch(
        `${API}/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to update notifications");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
      toast.success("Notification deleted");
    } catch (error) {
      toast.error("Failed to delete notification");
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "assessment_complete": return <Award className="w-5 h-5 text-green-500" />;
      case "scholarship_reminder": return <FileText className="w-5 h-5 text-blue-500" />;
      case "consultation_booked": return <Video className="w-5 h-5 text-purple-500" />;
      case "new_content": return <Bell className="w-5 h-5 text-indigo-500" />;
      case "system_update": return <Info className="w-5 h-5 text-gray-500" />;
      case "reminder": return <Calendar className="w-5 h-5 text-yellow-500" />;
      case "alert": return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent": return "bg-red-100 text-red-800 border-red-300";
      case "high": return "bg-orange-100 text-orange-800 border-orange-300";
      case "normal": return "bg-blue-100 text-blue-800 border-blue-300";
      case "low": return "bg-gray-100 text-gray-800 border-gray-300";
      default: return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000);

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <Card className="overflow-hidden">
          <CardHeader className="space-y-4">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <CardTitle className="text-xl sm:text-2xl flex items-center gap-2">
                  <Bell className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                  <span className="truncate">Notifications</span>
                </CardTitle>
                <CardDescription className="text-sm mt-1">
                  {unreadCount > 0 ? `You have ${unreadCount} unread` : "You're all caught up!"}
                </CardDescription>
              </div>
            </div>
            
            {/* Filter Buttons Row */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={filter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("all")}
                className="text-xs sm:text-sm"
              >
                All
              </Button>
              <Button
                variant={filter === "unread" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("unread")}
                className="text-xs sm:text-sm"
              >
                Unread {unreadCount > 0 && `(${unreadCount})`}
              </Button>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs sm:text-sm ml-auto"
                >
                  <CheckCheck className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Mark all read</span>
                  <span className="xs:hidden">Read all</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg">No notifications</p>
                <p className="text-gray-400 text-sm mt-2">
                  {filter === "unread" ? "You don't have any unread notifications" : "You'll see notifications here"}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <Card 
                    key={notification.id}
                    className={`transition-all hover:shadow-md cursor-pointer overflow-hidden ${
                      !notification.is_read ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : 'border-l-4 border-l-transparent'
                    }`}
                    onClick={() => {
                      if (!notification.is_read) {
                        markAsRead(notification.id);
                      }
                      if (notification.link) {
                        navigate(notification.link);
                      }
                    }}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start gap-2 sm:gap-4">
                        <div className="flex-shrink-0 mt-0.5">
                          {getIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          {/* Title and badges */}
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900 text-sm sm:text-base break-words">
                              {notification.title}
                            </h4>
                            {!notification.is_read && (
                              <Badge className="bg-blue-500 text-white text-[10px] sm:text-xs px-1.5 py-0">New</Badge>
                            )}
                          </div>
                          
                          {/* Message */}
                          <p className="text-gray-600 text-xs sm:text-sm mb-2 break-words line-clamp-3">
                            {notification.message}
                          </p>
                          
                          {/* Time and actions */}
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-gray-400 text-[10px] sm:text-xs">
                              {getTimeAgo(notification.created_at)}
                            </p>
                            <div className="flex gap-0.5 sm:gap-1 flex-shrink-0">
                              {!notification.is_read && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification.id);
                                  }}
                                  title="Mark as read"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(notification.id);
                                }}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Notifications;